import { Component, type ErrorInfo, type ReactNode } from "react";
import { env } from "@/shared/constants/env";
import { describeError } from "./describeError";
import { ErrorScreen } from "./ErrorScreen";

type Props = { children: ReactNode };
type State = { error: unknown | null };

/**
 * Dernier filet de sécurité — entoure TOUT, providers et routeur compris.
 *
 * ## Ce qu'il rattrape que `errorElement` ne voit pas
 * Les `errorElement` de React Router ne couvrent que ce qui se rend DANS les
 * routes. Une exception levée par un provider (client TanStack, dialogues,
 * feature flags) ou par le `RouterProvider` lui-même passe à côté d'eux, et
 * l'écran reste alors blanc — l'app entière est bloquée, sans un mot.
 *
 * ## Pourquoi une classe
 * Il n'existe toujours pas d'équivalent en hooks : `componentDidCatch` et
 * `getDerivedStateFromError` ne sont disponibles que sur un composant classe.
 * C'est la seule raison de cette forme, à ne pas « moderniser ».
 */
export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Le seul endroit où l'on voit encore la pile de composants React, que la
    // trace JS ne donne pas. Précieux pour localiser un plantage de rendu.
    if (env.isDev) console.error("[RootErrorBoundary]", error, info.componentStack);
    // Point de branchement d'un rapporteur d'erreurs (Sentry…) le jour venu :
    // c'est ici que passent TOUTES les exceptions de rendu de l'app.
  }

  render() {
    if (this.state.error == null) return this.props.children;

    return (
      <ErrorScreen
        title="Une erreur est survenue"
        message="L'application n'a pas pu démarrer correctement. Rechargez la page pour reprendre."
        detail={describeError(this.state.error)}
      />
    );
  }
}
